import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import * as XLSX from 'xlsx';
import { idKey } from './entityIds';
import { normalizeList, fetchAllPages } from './apiPagination';
import { userFacingError } from '../i18n/userFacingError';

const EXPIRING_DAYS = 30;

const STATUS_LABELS = {
  '1': 'Aguardando abertura',
  '2': 'Processo aberto',
  '3': 'Licença emitida',
  '4': 'Solicitada prorrogação',
  '5': 'Finalizado',
  '6': 'Suspensa',
  '7': 'Cassada',
};

function buildIdLabelMap(items, labelFn) {
  const m = {};
  for (const x of items || []) {
    if (x?.id == null) continue;
    m[idKey(x.id)] = labelFn(x);
  }
  return m;
}

function nomeFromMap(map, id) {
  if (id == null || id === '') return '';
  return map[idKey(id)] || '';
}

export { normalizeList };

function parseDate(v) {
  if (v == null || v === '') return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function classifyLicense(lic) {
  const d = parseDate(lic.validade);
  if (!d) return { bucket: 'sem_data', label: 'Sem validade' };
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (d < today) return { bucket: 'vencida', label: 'Vencida' };
  const diff = (d - today) / 86400000;
  if (diff <= EXPIRING_DAYS) return { bucket: 'proxima', label: 'Próxima ao vencimento' };
  return { bucket: 'em_dia', label: 'Em dia' };
}

function barRow(label, value, max, color) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return `
    <div style="margin-bottom:10px">
      <div style="display:flex;justify-content:space-between;font-size:12px;margin-bottom:4px">
        <span>${label}</span><span>${value}</span>
      </div>
      <div style="background:#e5e7eb;height:10px;border-radius:6px;overflow:hidden">
        <div style="width:${pct}%;background:${color};height:100%"></div>
      </div>
    </div>`;
}

function escHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function baseDoc(title, subtitle, inner, brand = {}) {
  const now = format(new Date(), "dd/MM/yyyy HH:mm", { locale: ptBR });
  const orgName = escHtml(brand.orgName || 'useAmbiental');
  const banner = brand.bannerUrl
    ? `<div class="banner"><img src="${escHtml(brand.bannerUrl)}" alt="" /></div>`
    : '';
  const arms = brand.coatOfArmsUrl
    ? `<img class="arms" src="${escHtml(brand.coatOfArmsUrl)}" alt="" />`
    : '';
  const logo = brand.logoUrl
    ? `<img class="wordmark" src="${escHtml(brand.logoUrl)}" alt="" />`
    : '';
  return `<!DOCTYPE html>
<html lang="pt-BR"><head><meta charset="utf-8"/><title>${escHtml(title)}</title>
<style>
  body{font-family:system-ui,-apple-system,sans-serif;margin:0;padding:32px;background:#f8fafc;color:#0f172a}
  .wrap{max-width:920px;margin:0 auto;background:#fff;border-radius:12px;box-shadow:0 4px 24px rgba(15,23,42,.08);overflow:hidden}
  .banner{background:#ffffff;display:flex;justify-content:center}
  .banner img{max-height:72px;max-width:100%;object-fit:contain}
  header{background:linear-gradient(135deg,#0f766e,#115e59);color:#fff;padding:20px 32px}
  .brand-row{display:flex;align-items:center;gap:14px}
  .arms{height:48px;width:48px;object-fit:contain;background:#fff;border-radius:10px;padding:3px}
  .wordmark{height:36px;max-width:180px;object-fit:contain;margin-left:auto;background:#fff;border-radius:8px;padding:4px}
  .powered{margin:0;font-size:10px;letter-spacing:.16em;text-transform:uppercase;opacity:.85}
  h1{margin:4px 0 0;font-size:22px;font-weight:700}
  .sub{margin:8px 0 0;font-size:13px;opacity:.9}
  .body{padding:28px 32px 36px}
  table{width:100%;border-collapse:collapse;font-size:13px;margin-top:16px}
  th,td{border:1px solid #e2e8f0;padding:8px 10px;text-align:left}
  th{background:#f1f5f9;font-weight:600}
  .muted{color:#64748b;font-size:12px}
  .grid2{display:grid;grid-template-columns:1fr 1fr;gap:24px;margin-top:20px}
  @media print{body{background:#fff}.wrap{box-shadow:none}}
</style></head><body>
<div class="wrap">
  ${banner}
  <header>
    <div class="brand-row">
      ${arms}
      <div>
        <p class="powered">useAmbiental · ${orgName}</p>
        <h1>${escHtml(title)}</h1>
      </div>
      ${logo}
    </div>
    <p class="sub">${escHtml(subtitle)}</p>
    <p class="sub" style="margin-top:12px">Emitido em ${now}</p>
  </header>
  <div class="body">${inner}</div>
</div></body></html>`;
}

/** Coleta dados para relatórios (frontend + APIs existentes). Pagina até esgotar (evita 422 por limit>1000). */
export async function fetchReportDataset({ licenseService, identityService }) {
  const [licencas, empreendimentos, tipos] = await Promise.all([
    fetchAllPages((params) => licenseService.getLicencas(params)),
    fetchAllPages((params) => identityService.getEnterprises(params)),
    fetchAllPages((params) => licenseService.getTiposLicenca(params)),
  ]);
  const tipoNome = buildIdLabelMap(tipos, (t) => t.nome || t.name || String(t.id));
  const entNome = buildIdLabelMap(empreendimentos, (e) => e.name || e.nome || String(e.id));
  return { licencas, empreendimentos, tipoNome, entNome };
}

function withinReferencePeriod(lic, period) {
  const raw = lic.data_abertura_processo || lic.created_at || lic.validade;
  if (!raw) return false;
  const when = new Date(raw);
  if (Number.isNaN(when.getTime())) return false;
  const start = new Date();
  if (period === 'last-month') start.setMonth(start.getMonth() - 1);
  else if (period === 'last-quarter') start.setMonth(start.getMonth() - 3);
  else if (period === 'last-year') start.setFullYear(start.getFullYear() - 1);
  else return true;
  return when >= start && when <= new Date();
}

export function filterLicenseDataset(dataset, { period = '', tipoId = '', status = '' } = {}) {
  const licencas = (dataset.licencas || []).filter((lic) => {
    if (status && String(lic.status) !== String(status)) return false;
    if (tipoId && String(lic.tipo_licenca_id) !== String(tipoId)) return false;
    if (period && !withinReferencePeriod(lic, period)) return false;
    return true;
  });
  return { ...dataset, licencas };
}

export function buildLicensesReportHtml(dataset, brand) {
  const { licencas, tipoNome, entNome } = dataset;
  const byStatus = {};
  const byClass = { vencida: 0, proxima: 0, em_dia: 0, sem_data: 0 };
  for (const lic of licencas) {
    const st = lic.status != null ? String(lic.status) : '—';
    byStatus[st] = (byStatus[st] || 0) + 1;
    const c = classifyLicense(lic).bucket;
    byClass[c] = (byClass[c] || 0) + 1;
  }
  const maxSt = Math.max(0, ...Object.values(byStatus));
  let bars = '';
  for (const [k, v] of Object.entries(byStatus)) {
    bars += barRow(`Status ${k}`, v, maxSt, '#0d9488');
  }
  const maxC = Math.max(1, ...Object.values(byClass));
  const classBars =
    barRow('Vencidas', byClass.vencida || 0, maxC, '#dc2626') +
    barRow('Próximas (30 dias)', byClass.proxima || 0, maxC, '#ca8a04') +
    barRow('Em dia', byClass.em_dia || 0, maxC, '#16a34a') +
    barRow('Sem data de validade', byClass.sem_data || 0, maxC, '#64748b');

  const rows = licencas
    .slice(0, 200)
    .map((lic) => {
      const cls = classifyLicense(lic).label;
      const tipo = lic.tipo_licenca_id ? nomeFromMap(tipoNome, lic.tipo_licenca_id) || '—' : '—';
      const emp = lic.empreendimento_id ? nomeFromMap(entNome, lic.empreendimento_id) || '—' : '—';
      const val = lic.validade ? String(lic.validade).slice(0, 10) : '—';
      return `<tr><td>${lic.numero || '—'}</td><td>${tipo}</td><td>${emp}</td><td>${val}</td><td>${cls}</td></tr>`;
    })
    .join('');

  const inner = `
    <p class="muted">Total de licenças: <strong>${licencas.length}</strong></p>
    <div class="grid2">
      <div><h2 style="font-size:15px;margin:0 0 8px">Distribuição por status</h2>${bars || '<p class="muted">Sem dados</p>'}</div>
      <div><h2 style="font-size:15px;margin:0 0 8px">Situação de vencimento</h2>${classBars}</div>
    </div>
    <h2 style="font-size:15px;margin:24px 0 8px">Amostra (até 200 registros)</h2>
    <table><thead><tr><th>Número</th><th>Tipo</th><th>Empreendimento</th><th>Validade</th><th>Classe</th></tr></thead>
    <tbody>${rows || '<tr><td colspan="5">Nenhum registro</td></tr>'}</tbody></table>`;

  return baseDoc('Relatório de licenças', 'Status, vencimentos e amostra analítica', inner, brand);
}

export function buildEnterprisesReportHtml(dataset, brand) {
  const { empreendimentos, licencas } = dataset;
  const licCount = {};
  for (const lic of licencas) {
    const id = lic.empreendimento_id;
    if (!id) continue;
    licCount[id] = (licCount[id] || 0) + 1;
  }
  const byType = {};
  for (const e of empreendimentos) {
    const t = e.type || '—';
    byType[t] = (byType[t] || 0) + 1;
  }
  const maxT = Math.max(0, ...Object.values(byType));
  let bars = '';
  for (const [k, v] of Object.entries(byType)) {
    bars += barRow(k, v, maxT, '#059669');
  }
  const rows = empreendimentos
    .slice(0, 200)
    .map((e) => {
      const n = e.name || e.nome || '—';
      const c = licCount[e.id] || 0;
      return `<tr><td>${n}</td><td>${e.cnpj || '—'}</td><td>${e.type || '—'}</td><td>${e.city || '—'}</td><td>${c}</td></tr>`;
    })
    .join('');

  const inner = `
    <p class="muted">Total de empreendimentos: <strong>${empreendimentos.length}</strong></p>
    <h2 style="font-size:15px;margin:16px 0 8px">Tipos cadastrados</h2>
    ${bars || '<p class="muted">Sem dados</p>'}
    <h2 style="font-size:15px;margin:24px 0 8px">Cadastros (até 200)</h2>
    <table><thead><tr><th>Nome</th><th>CNPJ</th><th>Tipo</th><th>Cidade</th><th>Licenças vinc.</th></tr></thead>
    <tbody>${rows || '<tr><td colspan="5">Nenhum registro</td></tr>'}</tbody></table>`;

  return baseDoc('Relatório de empreendimentos', 'Cadastros, tipos e vínculos com licenças', inner, brand);
}

export function buildExpiringReportHtml(dataset, brand) {
  const { licencas, tipoNome, entNome } = dataset;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const limit = new Date(today);
  limit.setDate(limit.getDate() + EXPIRING_DAYS);

  const soon = licencas.filter((lic) => {
    const d = parseDate(lic.validade);
    if (!d) return false;
    return d >= today && d <= limit;
  });
  const expired = licencas.filter((lic) => {
    const d = parseDate(lic.validade);
    return d && d < today;
  });

  const rows = soon
    .sort((a, b) => new Date(a.validade) - new Date(b.validade))
    .map((lic) => {
      const tipo = lic.tipo_licenca_id ? nomeFromMap(tipoNome, lic.tipo_licenca_id) || '—' : '—';
      const emp = lic.empreendimento_id ? nomeFromMap(entNome, lic.empreendimento_id) || '—' : '—';
      const val = lic.validade ? String(lic.validade).slice(0, 10) : '—';
      return `<tr><td>${lic.numero || '—'}</td><td>${tipo}</td><td>${emp}</td><td>${val}</td></tr>`;
    })
    .join('');

  const inner = `
    <p class="muted">Licenças vencidas (total): <strong>${expired.length}</strong> · 
    Próximas aos ${EXPIRING_DAYS} dias: <strong>${soon.length}</strong></p>
    <h2 style="font-size:15px;margin:20px 0 8px">Detalhe — vencimento em até ${EXPIRING_DAYS} dias</h2>
    <table><thead><tr><th>Número</th><th>Tipo</th><th>Empreendimento</th><th>Validade</th></tr></thead>
    <tbody>${rows || '<tr><td colspan="4">Nenhuma licença neste intervalo</td></tr>'}</tbody></table>`;

  return baseDoc('Relatório de vencimentos', `Licenças vencidas e janela de ${EXPIRING_DAYS} dias`, inner, brand);
}

/** Abre o HTML completo em nova aba (blob), com estilos preservados. */
export function previewReportHtml(html) {
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const w = window.open(url, '_blank', 'noopener,noreferrer');
  if (w) {
    setTimeout(() => URL.revokeObjectURL(url), 120000);
  } else {
    URL.revokeObjectURL(url);
  }
}

/**
 * Gera PDF no cliente a partir do mesmo HTML do relatório (renderização em iframe oculto).
 */
export async function downloadReportPdf(html, filenameBase = 'relatorio') {
  const mod = await import('html2pdf.js');
  const html2pdf = mod.default || mod;
  const iframe = document.createElement('iframe');
  iframe.setAttribute('title', 'relatorio-pdf-src');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.cssText =
    'position:fixed;left:-9999px;top:0;width:1024px;height:1px;overflow:hidden;border:0;opacity:0;pointer-events:none;';
  document.body.appendChild(iframe);
  const idoc = iframe.contentDocument || iframe.contentWindow?.document;
  if (!idoc) {
    document.body.removeChild(iframe);
    throw new Error('Não foi possível criar o documento para exportação PDF');
  }
  idoc.open();
  idoc.write(html);
  idoc.close();
  await new Promise((resolve) => {
    if (iframe.contentDocument?.readyState === 'complete') {
      resolve();
      return;
    }
    iframe.onload = () => resolve();
    setTimeout(resolve, 500);
  });
  await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
  const body = idoc.body;
  if (!body) {
    document.body.removeChild(iframe);
    throw new Error('Não foi possível renderizar o HTML para PDF');
  }
  const stamp = format(new Date(), 'yyyy-MM-dd-HHmm');
  const opt = {
    margin: [10, 10, 10, 10],
    filename: `${filenameBase}-${stamp}.pdf`,
    image: { type: 'jpeg', quality: 0.92 },
    html2canvas: { scale: 2, useCORS: true, logging: false },
    jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
    pagebreak: { mode: ['css', 'legacy'], avoid: ['tr'] },
  };
  try {
    await html2pdf().set(opt).from(body).save();
  } finally {
    document.body.removeChild(iframe);
  }
}

/**
 * Exporta planilha Excel (.xlsx, compatível com Excel/LibreOffice — formato moderno do ecossistema “xls”).
 */
export function downloadReportXlsx(dataset, kind, filenameBase = 'relatorio') {
  const stamp = format(new Date(), 'yyyy-MM-dd-HHmm');
  const fname = `${filenameBase}-${stamp}.xlsx`;
  const wb = XLSX.utils.book_new();
  const { licencas, empreendimentos, tipoNome, entNome } = dataset;
  const tn = (id) => nomeFromMap(tipoNome, id);
  const en = (id) => nomeFromMap(entNome, id);

  if (kind === 'licenses') {
    const rows = licencas.map((lic) => ({
      Número: lic.numero || '',
      Tipo: tn(lic.tipo_licenca_id),
      Empreendimento: en(lic.empreendimento_id),
      Validade: lic.validade ? String(lic.validade).slice(0, 10) : '',
      Status: STATUS_LABELS[String(lic.status)] || lic.status || '',
      'Classe vencimento': classifyLicense(lic).label,
    }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Licenças');
  } else if (kind === 'enterprises') {
    const licCount = {};
    for (const lic of licencas) {
      const eid = lic.empreendimento_id;
      if (!eid) continue;
      const k = idKey(eid);
      licCount[k] = (licCount[k] || 0) + 1;
    }
    const rows = empreendimentos.map((e) => ({
      Nome: e.name || e.nome || '',
      CNPJ: e.cnpj || '',
      Tipo: e.type || '',
      Cidade: e.city || '',
      'Licenças vinculadas': licCount[idKey(e.id)] || 0,
    }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Empreendimentos');
  } else if (kind === 'expiring') {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const lim = new Date(today);
    lim.setDate(lim.getDate() + EXPIRING_DAYS);
    const soon = licencas
      .filter((lic) => {
        const d = parseDate(lic.validade);
        if (!d) return false;
        return d >= today && d <= lim;
      })
      .sort((a, b) => new Date(a.validade) - new Date(b.validade));
    const rows = soon.map((lic) => ({
      Número: lic.numero || '',
      Tipo: tn(lic.tipo_licenca_id),
      Empreendimento: en(lic.empreendimento_id),
      Validade: lic.validade ? String(lic.validade).slice(0, 10) : '',
    }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), `Venc_${EXPIRING_DAYS}d`);
  }

  XLSX.writeFile(wb, fname);
}

/** Mensagem de produto para erros de relatório — sem detalhes internos. */
export function reportErrorMessage(error) {
  return userFacingError(error, 'reports:generateError');
}
